/**
 * Reglages > Suivi — ce qui s'est passe, jour par jour, et ce qui attend.
 *
 * Quatre blocs : les RDV pris dans Carvis (et lesquels ne sont pas encore sur ServiceBox /
 * dans Alpha), les WhatsApp (envois, reponses, echecs separes fixe / mobile — un 02 n'a pas
 * de WhatsApp, ce n'est pas une panne), les demandes de confirmation restees sans reponse,
 * et les operations ServiceBox / Alpha du proxy. Une seule requete backend, /activity/summary.
 */
import { useEffect, useMemo, useState } from "react";

import { Text1, Text2 } from "@components/ui/texts/Texts";
import { LineChart, type LineChartDataPoint } from "@components/charts/LineChart";
import { activityApi, type ActivitySummary } from "@services/api/activity";
import { navigateTo } from "context/navigation.service";

const PERIODS = [7, 14, 30] as const;

const fmtDay = (iso: string, withYear = false) => {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return d
    .toLocaleDateString("fr-FR", withYear ? { day: "2-digit", month: "short", year: "numeric" } : { day: "2-digit", month: "short" })
    .replace(".", "");
};

const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

function series<T extends { date: string }>(days: T[], pick: (d: T) => number): LineChartDataPoint[] {
  return days.map((d) => ({ label: fmtDay(d.date), tooltipLabel: fmtDay(d.date, true), value: pick(d) }));
}

function Kpi({ label, value, hint, tone = "neutral" }: { label: string; value: string | number; hint?: string; tone?: "neutral" | "good" | "bad" | "warn" }) {
  const palette = {
    neutral: { bg: "#F9FAFB", border: "#E5E7EB", text: "#111827" },
    good: { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534" },
    bad: { bg: "#FEF2F2", border: "#FECACA", text: "#991B1B" },
    warn: { bg: "#FFFBEB", border: "#FCD34D", text: "#92400E" },
  }[tone];
  return (
    <div className="flex flex-col flex-1 min-w-[150px] p-4 rounded-xl" style={{ background: palette.bg, border: `1px solid ${palette.border}` }}>
      <span className="text-h4 font-medium mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>{label}</span>
      <span className="text-hx font-bold" style={{ fontFamily: "Poppins", color: palette.text }}>{value}</span>
      {hint && <span className="text-h5 mt-1" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>{hint}</span>}
    </div>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <div className="p-5 rounded-xl" style={{ background: "#FAFAFA", border: "1px solid #E5E7EB" }}>{children}</div>;
}

function SectionTitle({ children, hint }: { children: React.ReactNode; hint?: string }) {
  return (
    <div className="flex flex-col mt-6 mb-3">
      <span className="text-h2 font-semibold" style={{ fontFamily: "Poppins", color: "#111827" }}>{children}</span>
      {hint && <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>{hint}</span>}
    </div>
  );
}

function ReasonList({ title, reasons, color }: { title: string; reasons: [string, number][]; color: string }) {
  return (
    <div className="flex flex-col gap-1 flex-1 min-w-[260px]">
      <span className="text-h4 font-medium mb-1" style={{ fontFamily: "Poppins", color: "#6B7280" }}>{title}</span>
      {reasons.length === 0 && <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>Aucun</span>}
      {reasons.map(([reason, n]) => (
        <div key={reason} className="flex flex-row items-start justify-between gap-3">
          <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>{reason}</span>
          <span className="text-h4 font-medium flex-shrink-0" style={{ fontFamily: "Poppins", color }}>{n}</span>
        </div>
      ))}
    </div>
  );
}

export function Suivi() {
  const [days, setDays] = useState<(typeof PERIODS)[number]>(14);
  const [data, setData] = useState<ActivitySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    activityApi
      .getSummary(days)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: any) => { if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Erreur de chargement"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  const rdvCharts = useMemo(() => data && ({
    created: series(data.rdv.days, (d) => d.created),
    onSb: series(data.rdv.days, (d) => d.on_servicebox),
    inAlpha: series(data.rdv.days, (d) => d.in_alpha),
  }), [data]);
  const waCharts = useMemo(() => data && ({
    sent: series(data.whatsapp.days, (d) => d.sent),
    inbound: series(data.whatsapp.days, (d) => d.inbound),
    failedMobile: series(data.whatsapp.days, (d) => d.failed_mobile - (d.failed_mobile_recovered ?? 0)),
    failedFixe: series(data.whatsapp.days, (d) => d.failed_fixe + d.failed_autre),
  }), [data]);
  const confCharts = useMemo(() => data && ({
    asked: series(data.confirmations.days, (d) => d.asked),
    unanswered: series(data.confirmations.days, (d) => d.unanswered_rate),
  }), [data]);
  const proxyCharts = useMemo(() => data && ({
    createRdv: series(data.proxy.days, (d) => d.create_rdv),
    transfers: series(data.proxy.days, (d) => d.transfer_alpha),
    failures: series(data.proxy.days, (d) => d.create_rdv_failed + d.transfer_alpha_failed + d.status_push_failed),
  }), [data]);

  const backlogSb = data?.rdv_backlog.filter((b) => b.missing === "servicebox") || [];
  const backlogAlpha = data?.rdv_backlog.filter((b) => b.missing === "alpha") || [];

  return (
    <div className="flex flex-col w-full max-w-1p2k px-4 lg:px-10 pt-10 pb-16 gap-2">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div className="flex flex-col">
          <Text1>Suivi</Text1>
          <Text2>Ce qui s'est passé, jour par jour — et ce qui attend encore.</Text2>
        </div>
        <div className="flex flex-row gap-1 p-1 rounded-lg" style={{ background: "#F3F4F6" }}>
          {PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setDays(p)}
              className="px-3 py-1.5 rounded-md text-h4 font-medium"
              style={{ fontFamily: "Poppins", background: days === p ? "#FFFFFF" : "transparent", color: days === p ? "#111827" : "#6B7280", boxShadow: days === p ? "0 1px 2px rgba(0,0,0,0.08)" : "none" }}
            >
              {p} jours
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-4 px-4 py-3 rounded-lg text-h4" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B", fontFamily: "Poppins" }}>{error}</div>
      )}
      {loading && !data && <span className="text-h4 mt-6" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>Chargement…</span>}

      {data && rdvCharts && waCharts && confCharts && proxyCharts && (
        <div className="flex flex-col" style={{ opacity: loading ? 0.6 : 1 }}>
          {/* ------------------------------------------------ KPI */}
          <div className="flex flex-row flex-wrap gap-3 mt-4">
            <Kpi label="RDV pris dans Carvis" value={data.rdv.totals.created} hint={`${days} jours`} />
            <Kpi label="En attente ServiceBox" value={backlogSb.length} hint="RDV à venir, pas encore transférés" tone={backlogSb.length ? "warn" : "good"} />
            <Kpi label="En attente Alpha" value={backlogAlpha.length} hint="sur ServiceBox, sans OR" tone={backlogAlpha.length ? "warn" : "good"} />
            <Kpi label="WhatsApp envoyés" value={data.whatsapp.totals.sent} hint={`${data.whatsapp.totals.inbound} réponses reçues`} />
            <Kpi label="Échecs sur mobile" value={data.whatsapp.totals.failed_mobile - (data.whatsapp.totals.failed_mobile_recovered ?? 0)} hint={`06/07 jamais rejoints — ${data.whatsapp.totals.failed_mobile_recovered ?? 0} autre(s) rattrapé(s) ensuite`} tone={data.whatsapp.totals.failed_mobile - (data.whatsapp.totals.failed_mobile_recovered ?? 0) ? "bad" : "good"} />
            <Kpi label="Échecs sur fixe" value={data.whatsapp.totals.failed_fixe + data.whatsapp.totals.failed_autre} hint="01-05/09 ou étranger — pas de WhatsApp" tone="neutral" />
            <Kpi label="Confirmations sans réponse" value={`${data.confirmations.totals.unanswered_rate} %`} hint={`${data.confirmations.totals.unanswered} sur ${data.confirmations.totals.asked - data.confirmations.totals.failed - data.confirmations.totals.pending} livrées`} tone={data.confirmations.totals.unanswered_rate > 30 ? "warn" : "neutral"} />
          </div>

          {/* ------------------------------------------------ RDV */}
          <SectionTitle hint="Ordres créés dans Carvis (réservation en ligne ou saisie), hors imports ServiceBox.">Rendez-vous</SectionTitle>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <Card><LineChart data={rdvCharts.created} title="RDV pris" valueSuffix=" RDV" color="#2563EB" emptyMessage="Aucun RDV" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card><LineChart data={rdvCharts.onSb} title="Transférés sur ServiceBox" valueSuffix=" RDV" color="#7C3AED" emptyMessage="Aucun" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card><LineChart data={rdvCharts.inAlpha} title="Dans Alpha (OR créé)" valueSuffix=" OR" color="#059669" emptyMessage="Aucun" formatValue={(v) => `${v}`} height={150} /></Card>
          </div>
          {data.rdv_backlog.length > 0 && (
            <div className="mt-3">
              <Card>
                <div className="flex flex-row items-baseline justify-between mb-3">
                  <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#111827" }}>RDV à venir pas encore transférés</span>
                  <span className="text-h4" style={{ fontFamily: "Poppins", color: "#9CA3AF" }}>{data.rdv_backlog.length} ordre(s)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-h4" style={{ fontFamily: "Poppins" }}>
                    <thead>
                      <tr style={{ color: "#6B7280" }} className="text-left">
                        <th className="py-1 pr-3 font-medium">Réception</th>
                        <th className="py-1 pr-3 font-medium">N°</th>
                        <th className="py-1 pr-3 font-medium">Client</th>
                        <th className="py-1 pr-3 font-medium">Immat.</th>
                        <th className="py-1 pr-3 font-medium">Pris le</th>
                        <th className="py-1 font-medium">Il manque</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rdv_backlog.map((b) => (
                        <tr key={b.id} className="cursor-pointer hover:bg-neutral-100" style={{ borderTop: "1px solid #E5E7EB", color: "#111827" }} onClick={() => navigateTo({ route: `/ordre/${b.id}` })}>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{fmtDateTime(b.reception_date)}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{b.num || "—"}</td>
                          <td className="py-1.5 pr-3">{b.client_name || "—"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap">{b.immatriculation || "—"}</td>
                          <td className="py-1.5 pr-3 whitespace-nowrap" style={{ color: "#6B7280" }}>{fmtDateTime(b.created_at)}</td>
                          <td className="py-1.5">
                            <span className="px-2 py-0.5 rounded-md text-h5 font-medium" style={{ background: b.missing === "servicebox" ? "#FFFBEB" : "#EFF6FF", color: b.missing === "servicebox" ? "#92400E" : "#1D4ED8" }}>
                              {b.missing === "servicebox" ? "ServiceBox" : "Alpha (OR)"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          )}

          {/* ------------------------------------------------ WhatsApp */}
          <SectionTitle hint="Journal des communications. Un échec sur un fixe (01-05/09) est attendu : ces numéros n'ont pas de WhatsApp.">WhatsApp</SectionTitle>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Card><LineChart data={waCharts.sent} title="Messages envoyés" valueSuffix=" msg" color="#25D366" emptyMessage="Aucun message" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card><LineChart data={waCharts.inbound} title="Réponses reçues" valueSuffix=" msg" color="#0EA5E9" emptyMessage="Aucune réponse" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card>
              <LineChart data={waCharts.failedMobile} title="Échecs sur mobile (06/07) jamais rattrapés — à regarder" valueSuffix=" erreur(s)" color="#EF4444" emptyMessage="Aucun échec sur mobile" formatValue={(v) => `${v}`} height={150} />
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid #E5E7EB" }}>
                <ReasonList title="Motifs (mobiles)" reasons={data.whatsapp_failures.reasons_by_line.mobile || []} color="#991B1B" />
              </div>
            </Card>
            <Card>
              <LineChart data={waCharts.failedFixe} title="Échecs sur fixe / étranger — attendus" valueSuffix=" erreur(s)" color="#9CA3AF" emptyMessage="Aucun" formatValue={(v) => `${v}`} height={150} />
              <div className="mt-4 pt-4" style={{ borderTop: "1px solid #E5E7EB" }}>
                <ReasonList title="Motifs (fixes / autres)" reasons={[...(data.whatsapp_failures.reasons_by_line.fixe || []), ...(data.whatsapp_failures.reasons_by_line.autre || [])]} color="#6B7280" />
              </div>
            </Card>
          </div>

          {/* ------------------------------------------------ Confirmations */}
          <SectionTitle hint="Messages qui demandent au client de confirmer son RDV. « Répondu » = un message du client est arrivé ensuite dans la conversation. Ceux du jour ne sont pas encore comptés.">Confirmations de RDV</SectionTitle>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <Card><LineChart data={confCharts.asked} title="Demandes de confirmation envoyées" valueSuffix=" msg" color="#F59E0B" emptyMessage="Aucune demande" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card>
              <LineChart data={confCharts.unanswered} title="Part restée sans réponse" valueSuffix=" %" color="#DC2626" emptyMessage="Aucune demande" formatValue={(v) => `${v} %`} height={150} headline={data.confirmations.totals.unanswered_rate} />
              <div className="flex flex-row flex-wrap gap-4 mt-3 text-h4" style={{ fontFamily: "Poppins", color: "#6B7280" }}>
                <span>Envoyées : <b style={{ color: "#111827" }}>{data.confirmations.totals.asked}</b></span>
                <span>Répondues : <b style={{ color: "#166534" }}>{data.confirmations.totals.answered}</b></span>
                <span>Sans réponse : <b style={{ color: "#991B1B" }}>{data.confirmations.totals.unanswered}</b></span>
                <span>Non livrées : <b>{data.confirmations.totals.failed}</b></span>
                <span>Aujourd'hui, en attente : <b>{data.confirmations.totals.pending}</b></span>
              </div>
            </Card>
          </div>

          {/* ------------------------------------------------ ServiceBox / Alpha */}
          <SectionTitle hint="Opérations du proxy ServiceBox : créations de RDV, transferts vers Alpha, annulations, poussées de statut.">ServiceBox / Alpha</SectionTitle>
          <div className="flex flex-row flex-wrap gap-3 mb-3">
            <Kpi label="RDV créés sur ServiceBox" value={data.proxy.totals.create_rdv} hint={data.proxy.totals.create_rdv_failed ? `${data.proxy.totals.create_rdv_failed} refus` : "aucun refus"} tone={data.proxy.totals.create_rdv_failed ? "warn" : "neutral"} />
            <Kpi label="Transferts Alpha" value={data.proxy.totals.transfer_alpha} hint={data.proxy.totals.transfer_alpha_failed ? `${data.proxy.totals.transfer_alpha_failed} échec(s)` : "aucun échec"} tone={data.proxy.totals.transfer_alpha_failed ? "bad" : "neutral"} />
            <Kpi label="RDV annulés sur ServiceBox" value={data.proxy.totals.delete_rdv} />
            <Kpi label="Statuts refusés par ServiceBox" value={data.proxy.totals.status_push_failed} tone={data.proxy.totals.status_push_failed ? "warn" : "neutral"} />
            <Kpi label="Ordres supprimés" value={data.deletions.totals.deleted} hint="trace conservée" />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <Card><LineChart data={proxyCharts.createRdv} title="RDV créés sur ServiceBox" valueSuffix=" RDV" color="#7C3AED" emptyMessage="Aucun" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card><LineChart data={proxyCharts.transfers} title="Transferts vers Alpha" valueSuffix=" transfert(s)" color="#059669" emptyMessage="Aucun" formatValue={(v) => `${v}`} height={150} /></Card>
            <Card><LineChart data={proxyCharts.failures} title="Refus / échecs (RDV, Alpha, statuts)" valueSuffix=" échec(s)" color="#EF4444" emptyMessage="Aucun échec" formatValue={(v) => `${v}`} height={150} /></Card>
          </div>
          {data.deletions.recent.length > 0 && (
            <div className="mt-3">
              <Card>
                <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#111827" }}>Derniers ordres supprimés</span>
                <div className="flex flex-col gap-1 mt-2 text-h4" style={{ fontFamily: "Poppins" }}>
                  {data.deletions.recent.slice(0, 15).map((d) => (
                    <div key={d.ordre_id} className="flex flex-row flex-wrap gap-3" style={{ color: "#6B7280" }}>
                      <span className="whitespace-nowrap">{fmtDateTime(d.deleted_at)}</span>
                      <span style={{ color: "#111827" }}>{d.num || d.ordre_id}</span>
                      <span>{d.client_name || ""}</span>
                      <span className="ml-auto">{d.deleted_by || "?"}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
